import { useState, useEffect } from "react";
import useSWR from "swr";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { Field } from "@/pages/admin/ContentPanels";

export default function SettingsPanel() {
  const { data, mutate } = useSWR("/settings", (url) => adminApi.get(url));
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data && !form) {
      setForm({
        ...data,
        social: data.social || {},
        analytics: data.analytics || {},
        markets_text: (data.markets || []).map((m) => `${m.name} | ${m.description}`).join("\n"),
      });
    }
  }, [data, form]);

  if (!form) return <div className="card h-64 animate-pulse" />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSocial = (k) => (e) => setForm((f) => ({ ...f, social: { ...f.social, [k]: e.target.value } }));
  const setAnalytics = (k) => (e) => setForm((f) => ({ ...f, analytics: { ...f.analytics, [k]: e.target.value } }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        markets: form.markets_text.split("\n").filter(Boolean).map((l) => {
          const [name, ...rest] = l.split("|");
          return { name: (name || "").trim(), description: rest.join("|").trim() };
        }),
      };
      delete payload.markets_text;
      await adminApi.put("/admin/settings", payload);
      toast.success("Settings saved");
      mutate(payload, { revalidate: false });
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-settings-panel">
      <section className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Company Information</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Company Name"><input className="field" value={form.company_name || ""} onChange={set("company_name")} data-testid="settings-company-name" /></Field>
          <Field label="Tagline"><input className="field" value={form.tagline || ""} onChange={set("tagline")} data-testid="settings-tagline" /></Field>
          <Field label="Phone"><input className="field" value={form.phone || ""} onChange={set("phone")} data-testid="settings-phone" /></Field>
          <Field label="WhatsApp Number"><input className="field" value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="settings-whatsapp" /></Field>
          <Field label="Email"><input className="field" value={form.email || ""} onChange={set("email")} data-testid="settings-email" /></Field>
          <Field label="Address"><input className="field" value={form.address || ""} onChange={set("address")} data-testid="settings-address" /></Field>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Key Export Contact</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Name"><input className="field" value={form.key_person_name || ""} onChange={set("key_person_name")} /></Field>
          <Field label="Title"><input className="field" value={form.key_person_title || ""} onChange={set("key_person_title")} placeholder="[TO BE UPDATED]" /></Field>
          <Field label="Mobile / WhatsApp"><input className="field" value={form.key_person_phone || ""} onChange={set("key_person_phone")} /></Field>
          <Field label="Photo URL"><input className="field" value={form.key_person_photo || ""} onChange={set("key_person_photo")} /></Field>
          <Field label="Logo URL"><input className="field" value={form.logo_url || ""} onChange={set("logo_url")} /></Field>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Social Media</h2>
        <p className="mt-1 text-xs text-mist">Icons appear in the footer only when a real URL is provided.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {["linkedin", "instagram", "facebook", "twitter"].map((k) => (
            <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
              <input className="field" value={form.social[k] || ""} onChange={setSocial(k)} placeholder={`https://${k}.com/orvellaoverseas`} />
            </Field>
          ))}
        </div>
      </section>

      <section className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Analytics & Tracking</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Google Analytics ID (G-…)"><input className="field" value={form.analytics.ga_id || ""} onChange={setAnalytics("ga_id")} placeholder="G-XXXXXXXXXX" /></Field>
          <Field label="Google Search Console Verification"><input className="field" value={form.analytics.gsc_id || ""} onChange={setAnalytics("gsc_id")} /></Field>
          <Field label="Meta Pixel ID"><input className="field" value={form.analytics.meta_pixel || ""} onChange={setAnalytics("meta_pixel")} /></Field>
          <Field label="LinkedIn Insight Tag ID"><input className="field" value={form.analytics.linkedin_partner || ""} onChange={setAnalytics("linkedin_partner")} /></Field>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Product Catalogue</h2>
        <div className="mt-4 grid gap-4">
          <Field label="Catalogue PDF URL">
            <input className="field" value={form.catalogue_url || ""} onChange={set("catalogue_url")} placeholder="Upload via Files & certificates panel or paste URL" data-testid="settings-catalogue-url" />
          </Field>
          <Field label="Request buyer info before download?">
            <select className="field" value={String(form.catalogue_requires_info)} onChange={(e) => setForm((f) => ({ ...f, catalogue_requires_info: e.target.value === "true" }))}>
              <option value="true">Yes — ask name, company, country, email</option>
              <option value="false">No — direct download</option>
            </select>
          </Field>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Bank Transfer / Payment Details</h2>
        <p className="mt-1 text-xs leading-relaxed text-mist">
          Shown on the private payment page for each payment request (Admin → Payments). Buyers use these details to pay by
          bank transfer; you then mark the request as Paid and a receipt email is sent automatically.
        </p>
        <textarea
          className="field mt-3 min-h-32"
          rows={6}
          value={form.payment_instructions || ""}
          onChange={set("payment_instructions")}
          data-testid="settings-payment-instructions"
          placeholder={"Bank: [your bank name]\nA/C Name: Orvella Overseas Private Limited\nA/C No: [account number]\nIFSC: [ifsc code]\nSWIFT: [swift code for international buyers]\nUPI: [upi id]"}
        />
      </section>

      <section className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Target Markets</h2>
        <p className="mt-1 text-xs text-mist">One per line — Name | Short description</p>
        <textarea className="field mt-3 min-h-40" rows={6} value={form.markets_text || ""} onChange={set("markets_text")} data-testid="settings-markets" />
      </section>

      <button onClick={save} disabled={busy} className="btn-gold" data-testid="settings-save-btn">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Settings
      </button>
    </div>
  );
}
